import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../navbar/navbar';
import Footer from '../../footer/footer';
import Spinner from '../../spinner/spinner';
import { FaInfoCircle, FaHandsHelping, FaEnvelope } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import image1 from '../../../assets/images/aiease_1754765405156.jpg';
import image2 from '../../../assets/images/1717936604753-ggd4.webp';
import image3 from '../../../assets/images/IMG-20250316-WA0147.jpg';
import image4 from '../../../assets/images/IMG_8549.jpg';
import image5 from '../../../assets/images/IMG_2078 (1).jpg';
import image6 from '../../../assets/images/IMG-20241007-WA0063(1).jpg';
import './home.css';

export default function Home() {
  const [currentImage, setCurrentImage] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [loading, setLoading] = useState(true);

  const images = [image1, image2, image3, image4, image5, image6];
  const newsTitles = [
    "AbsaxDziko Climate Innovation Workshop",
    "Young Eco-Warriors Upcycling Project",
    "Ashanti Regional Tour"
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    const rotate = setInterval(() => {
      if (autoRotate) setCurrentImage(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(rotate);
  }, [autoRotate]);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) return <Spinner />;

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Home - Dziko Centre for Climate Justice</title>
        <meta
          name="description"
          content="Empowering youth to lead climate action through education, innovation, and sustainability projects."
        />
      </Helmet>

      {/* Navbar */}
      <Navbar transparentOnTop />

      {/* Hero */}
      <section className="relative h-screen overflow-hidden">
        {images.slice(0, 3).map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${currentImage === index ? 'opacity-100' : 'opacity-0'}`}
          >
            <img src={img} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
          </div>
        ))}

        {/* Dots */}
        <div className="absolute bottom-8 left-0 w-full flex justify-center gap-2">
          {images.slice(0, 3).map((_, index) => (
            <button
              key={index}
              className={`h-3 w-3 rounded-full transition-all ${currentImage === index ? 'bg-white scale-110' : 'bg-gray-400 hover:bg-white/70'}`}
              onClick={() => { setCurrentImage(index); setAutoRotate(false); }}
            />
          ))}
        </div>

        {/* Text */}
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4">
          <h1 className="text-4xl lg:text-6xl font-extrabold text-white drop-shadow-lg mb-4 animate-fadeIn">
            Empowering Youth for Climate Justice
          </h1>
          <p className="text-lg lg:text-xl text-gray-200 max-w-2xl mb-6 animate-fadeIn delay-200">
            Building a sustainable future through education, action, and innovation.
          </p>
          <Link
            to="/about"
            className="bg-indigo-700 hover:bg-indigo-500 text-white py-3 px-8 rounded-full font-semibold shadow-lg transition-transform transform hover:scale-105"
          >
            Learn More
          </Link>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-3xl lg:text-4xl font-bold text-indigo-800 mb-6">Our Mission</h2>
        <p className="text-lg text-gray-700 mb-8 leading-relaxed">
          At Dziko Centre for Climate Justice (Dziko CCJ), we equip young people with tools
          to fight climate change through upcycling, climate-smart agriculture, clean energy
          exploration, and reforestation. We are committed to excellence, transparency, and
          building community resilience.
        </p>
        <Link
          to="/about"
          className="bg-indigo-800 hover:bg-indigo-500 text-white py-2 px-6 rounded-full shadow-md transition-transform transform hover:scale-105"
        >
          Discover More
        </Link>
      </section>

      {/* Cards */}
      <section id="projects" className="py-20 grid gap-10 md:grid-cols-3 px-6 max-w-7xl mx-auto">
        {[
          { icon: <FaInfoCircle />, title: 'Our Projects', text: 'Initiatives tackling deforestation, plastic pollution, and climate change.', link: '/projects', linkText: 'View Projects →' },
          { icon: <FaHandsHelping />, title: 'Support Us', text: 'Fund our projects or volunteer to plant trees, organize events, and raise awareness.', link: '/support', linkText: 'Get Involved →' },
          { icon: <FaEnvelope />, title: 'Contact Us', text: 'Let’s work together for a greener planet. We’d love to hear from you.', link: 'mailto:info@dzikoccj.org', linkText: 'Get in Touch →' }
        ].map((card, i) => (
          <div key={i} className="bg-indigo-50 rounded-2xl shadow-lg p-8 text-center hover:shadow-2xl hover:-translate-y-1 transition-all">
            <div className="text-5xl text-indigo-800 mx-auto mb-4">{card.icon}</div>
            <h3 className="text-2xl font-bold mb-4">{card.title}</h3>
            <p className="text-gray-700 mb-6">{card.text}</p>
            {card.link.startsWith('mailto') ? (
              <a href={card.link} className="text-indigo-700 font-semibold hover:underline">{card.linkText}</a>
            ) : (
              <Link to={card.link} className="text-indigo-700 font-semibold hover:underline">{card.linkText}</Link>
            )}
          </div>
        ))}
      </section>

      {/* News */}
      <section className="py-20 text-center px-6 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-indigo-800 mb-4">News & Events</h2>
        <p className="text-lg text-gray-700 mb-8">Stay updated on our latest campaigns and environmental initiatives.</p>
        <div className="grid gap-8 md:grid-cols-3">
          {images.slice(3, 6).map((img, index) => (
            <Link key={index} to="/projects" className="block bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all">
              <img src={img} alt={newsTitles[index]} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="text-lg font-semibold">{newsTitles[index]}</h3>
              </div>
            </Link>
          ))}
        </div>
        <Link to="/projects" className="inline-block mt-10 bg-indigo-800 hover:bg-indigo-500 text-white py-2 px-6 rounded-full shadow-md transition-transform transform hover:scale-105">
          View More
        </Link>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
